/**
 * Changes (/changes), under Docs › Requirements: every change the member may
 * read, newest first, one row each, with its repository, its size, the state of
 * each part of its brief and what waits for the owner (GET /changes, ADR-218).
 * The legend says how a change moves — four stages, two approvals — once.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Waypoints } from 'lucide-react';
import { getUserFriendlyErrorMessage } from '@puglieseweb/shared-observability';
import { BreadcrumbBar } from '@/components/editor/BreadcrumbBar';
import { SizeBadge, StateBadge } from '@/components/changes/changeBadges';
import { StageLegend, StageStripCompact } from '@/components/changes/StageStrip';
import { Spinner } from '@systemdox/ui';
import { captureError } from '@/lib/sentry';
import {
  PARTS,
  PART_LABEL,
  STAGES,
  STAGE_LABEL,
  changePath,
  countWaiting,
  formatDay,
  stageOf,
  stageStates,
  type ChangeSummary,
  type Stage,
} from '@/lib/changes/model';
import { cn } from '@/lib/utils';
import { changesApi } from '@/services/changesApi';

type Filter = Stage | 'all';

/** The state of each of the four parts; a small change has no decision part. */
function PartStates({ change }: { change: ChangeSummary }) {
  return (
    <ul className="flex flex-wrap gap-x-3 gap-y-1">
      {PARTS.map((part) => {
        const state = change.states[part];
        return (
          <li key={part} data-part-state={part} className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
            {PART_LABEL[part]}
            {state ? <StateBadge state={state} /> : <span className="text-slate-400 dark:text-slate-500">no decision record</span>}
          </li>
        );
      })}
    </ul>
  );
}

export default function ChangesListPage() {
  const { search } = useLocation();
  const [filter, setFilter] = useState<Filter>('all');
  const [changes, setChanges] = useState<ChangeSummary[] | null>(null);
  const [failed, setFailed] = useState('');

  useEffect(() => {
    let live = true;
    changesApi.listAllChanges({ archived: true }).then(
      (items) => {
        if (live) setChanges([...items].sort((a, b) => b.lastActivity.localeCompare(a.lastActivity)));
      },
      (error) => {
        captureError(error, { component: 'ChangesListPage' });
        if (live) setFailed(getUserFriendlyErrorMessage(error));
      },
    );
    return () => { live = false; };
  }, []);

  const all = changes ?? [];
  const counts = Object.fromEntries(STAGES.map((s) => [s, all.filter((c) => stageOf(c) === s).length])) as Record<Stage, number>;
  const shown = filter === 'all' ? all : all.filter((c) => stageOf(c) === filter);
  const waiting = all.reduce((n, c) => n + countWaiting(c), 0);

  return (
    <div className="p-8">
      <BreadcrumbBar
        className="-mx-8 -mt-8 mb-6"
        crumbs={[
          { label: 'Docs', to: { pathname: '/docs', search } },
          { label: 'Requirements', to: { pathname: '/docs/requirements', search } },
          { label: 'Changes' },
        ]}
      />

      <div className="mb-6 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500">
          <Waypoints className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Changes</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Every change in your connected repositories, one page each: the brief and the release, each part with its state read from GitHub.{' '}
            {changes && (
              <span className="text-slate-500 dark:text-slate-400">
                {all.length} changes · {waiting} waiting for your review
              </span>
            )}
          </p>
        </div>
      </div>

      <StageLegend className="mb-6" />

      {failed && (
        <p role="alert" data-changes-failed className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
          {failed}
        </p>
      )}

      {!changes && !failed && (
        <div className="flex justify-center py-12">
          <Spinner aria-label="Loading changes" />
        </div>
      )}

      {changes && all.length === 0 && (
        <div data-no-changes className="rounded-xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-900">
          <Waypoints className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-700" />
          <h2 className="mt-4 font-semibold text-slate-900 dark:text-slate-100">No change yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
            A change is a folder <code className="font-mono text-xs">specs/changes/&lt;slug&gt;/</code> in a connected repository. Connect one under{' '}
            <Link to={{ pathname: '/settings', search }} className="font-medium text-violet-700 hover:underline dark:text-violet-300">Settings › GitHub</Link>.
          </p>
        </div>
      )}

      {changes && all.length > 0 && (
        <>
          <div className="mb-3 flex flex-wrap gap-1.5" role="tablist" aria-label="Filter by stage">
            {(['all', ...STAGES] as Filter[]).map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={filter === f}
                onClick={() => setFilter(f)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  filter === f
                    ? 'bg-violet-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700',
                )}
              >
                {f === 'all' ? `All ${all.length}` : `${STAGE_LABEL[f]} ${counts[f]}`}
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <p className="py-12 text-center text-sm text-slate-400">No change in {filter === 'all' ? 'any stage' : STAGE_LABEL[filter]}.</p>
          ) : (
            <ul className="space-y-2" data-changes>
              {shown.map((change) => {
                const stage = stageOf(change);
                const waitingHere = countWaiting(change);
                return (
                  <li key={change.key}>
                    <Link
                      to={{ pathname: changePath(change), search }}
                      data-change={`${change.owner}/${change.repo}#${change.slug}`}
                      className="group flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
                    >
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{change.title}</h2>
                        <SizeBadge size={change.size} />
                        {waitingHere > 0 && <StateBadge state="waiting for your review" className="ml-auto" />}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                        <code className="font-mono">
                          {change.owner}/{change.repo} · {change.slug}
                        </code>
                        <StageStripCompact current={stage} states={stageStates(change)} />
                        <span className="ml-auto flex items-center gap-1">
                          Last activity {formatDay(change.lastActivity)}
                          <ArrowRight className="h-3.5 w-3.5 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-slate-500 dark:text-slate-600" />
                        </span>
                      </div>
                      <PartStates change={change} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
